import { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import Input from '../../../../components/form-control/Input';
import SelectMenu, { MiniSelectMenu } from '../../../../components/form-control/SelectMenu';
import SelectDropdown from '../../../../components/form-control/SelectDropdown';
import InputSelectMenu from '../../../../components/form-control/InputSelectMenu';
import { getDriverLicenseProviderByCountry, getDVLAOrFRSC, getNatureOfInterestListByCountry, getSelectableManufacturingYears, removeUnderscoresAndCapitalize } from '../../../../utils/miscFunctions';
import { useSelector } from 'react-redux/es/hooks/useSelector';
import { getEnumsFromState, getSelectedCountry } from '../../../../states/slices/resourceSlice';
import { IEnum } from '../../../../resources/interfaces';
import { IMultiSelectItem, MultiSelect } from '../../../../components/form-control/MultiSelect';
import { searchVehicleMake, searchVehicleModelsByMake } from '../../../../api/requests';
import { lookupVehicleBodyType } from '../../../../resources/vehicleBodyTypeMap';
import { useQuery } from '@tanstack/react-query';
import TextArea from '../../../../components/form/TextArea';
import { InputRadio } from '../../../../components/form/InputRadio';
import { Car, Plus, Settings, Trash2, Users } from 'lucide-react';
import { useFieldGate } from '../../../../contexts/PolicyEditPermissionsContext';
import { POLICY_EDIT_FIELD } from '../../../../utils/policyEditPermissions';
import IntlPhoneInput from '../../../../components/form-control/IntlPhoneInput/IntlPhoneInput';
import { getQuoteWidgetPayloadFromState } from '../../../../states/slices/quoteWidgetSlice';
import usePartner from '../../../../hooks/partnerHook';


type IVehicleInformationForm = {
    showModificationFields?: boolean
    showInterestedPartiesFields?: boolean
    showNamedDriverFields?: boolean
    showVehicleUsages?: boolean;
    showOnlyMakeModelfields?: boolean;
    disabledFields?: [{fieldName: string, disabled: boolean}]
    setFields?:[{fieldName: string, value: string | number | null}];
}

const VehicleInformationForm = ({disabledFields, setFields, showOnlyMakeModelfields = false, showModificationFields = true, showInterestedPartiesFields = true, showNamedDriverFields = true, showVehicleUsages = false }: IVehicleInformationForm) => {
    const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldError } = useFormikContext<any>();
    const selectedCountry = useSelector(getSelectedCountry);
    const enums = useSelector(getEnumsFromState)
    const [selectedVehicleMake, setSelectedVehicleMake] = useState<string | null>(values.make || null);
    const [vehicleMakes, setVehicleMakes] = useState<string[]>([]);
    const [vehicleMakePrefix, setVehicleMakePrefix] = useState<string>('');
    const [loadingMakes, setLoadingMakes] = useState<boolean>(false);
    const [vehicleModels, setVehicleModels] = useState<any>([]);
    const [validManufacturingYears, setValidManufacturingYears] = useState(getSelectableManufacturingYears());
    // Per-field gates read from PolicyEditPermissionsContext. On surfaces
    // that don't wrap this form in a provider (e.g. the create wizard's
    // draft flow), useFieldGate returns the default 'draft' permissions —
    // i.e. every field editable — so the form behaves as it did before.
    const makeGate = useFieldGate(POLICY_EDIT_FIELD.VEHICLE_MAKE);
    const modelGate = useFieldGate(POLICY_EDIT_FIELD.VEHICLE_MODEL);
    const sumInsuredGate = useFieldGate(POLICY_EDIT_FIELD.SUM_INSURED);
    const usageGate = useFieldGate(POLICY_EDIT_FIELD.VEHICLE_USAGE);
    const dataFromQuoteCalculation = useSelector(getQuoteWidgetPayloadFromState);
     const { fetchCompanyPortfolio } = usePartner()
    const insurerPortfolioIdFromQuote = dataFromQuoteCalculation?.PAYLOAD?.insurerPortfolioId;
    const selectedInsurerIdFromQuote = dataFromQuoteCalculation?.PAYLOAD?.insurerId;
    const { data: insurerPortfolio = [] } = useQuery({
        queryKey: ['insurerPortfolio', selectedInsurerIdFromQuote],
        queryFn: () => {
          
            return fetchCompanyPortfolio(Number(selectedInsurerIdFromQuote));
        },
        enabled: !!selectedInsurerIdFromQuote,
    })

    const coverTypeNameFromQuote = (() => {
        const id = Number(insurerPortfolioIdFromQuote);
        if (!id) return '';
        const match = insurerPortfolio && insurerPortfolio?.find((p: any) => Number(p?.id) === id || String(p?.key) === String(id));
        return String(match?.title ?? match?.name ?? '').toLowerCase();
    })();

    const isComprehensiveCover = coverTypeNameFromQuote.includes('comprehensive');
    const isThirdPartyFireTheftCover = (() => {
        const normalized = coverTypeNameFromQuote.replace(/\s+/g, ' ').trim();
        return (
            normalized.includes('third party fire') ||
            normalized.includes('third-party fire') ||
            normalized.includes('third party fire & theft') ||
            normalized.includes('third party fire and theft')
        );
    })();
    const isCoverTypeResolved = !dataFromQuoteCalculation?.IS_QUOTE_CALCULATOR
        ? true
        : !insurerPortfolioIdFromQuote
            ? true
            : !!coverTypeNameFromQuote;

    const shouldShowSumInsured = !dataFromQuoteCalculation?.IS_QUOTE_CALCULATOR
        ? true
        : !isCoverTypeResolved
            ? true
            : isComprehensiveCover || isThirdPartyFireTheftCover;

    useEffect(() => {
        if (!isCoverTypeResolved) return;
        if (shouldShowSumInsured) return;
        setFieldError('sumInsured', undefined as any);
        setFieldValue('sumInsured', undefined);
    }, [isCoverTypeResolved, setFieldError, setFieldValue, shouldShowSumInsured]);

    const seachForVehicleMake = async (prefix: string) => {
        try {
            const makes = await searchVehicleMake(prefix?.toUpperCase());
            if (makes) {
                setVehicleMakes((makes?.data || []).slice(0, 100));
            }
        } catch (error) {
        } finally {
            setLoadingMakes(false);
        }
    };

    useEffect(() => {
        if (selectedVehicleMake) {
            setVehicleModels([]);
            seachForVehicleModel();
        }
    }, [selectedVehicleMake]);

    // Sync internal selectedVehicleMake state with the formik value, so
    // programmatic prefills (e.g. from the reg/VIN auto-lookup) trigger the
    // model dropdown to repopulate.
    useEffect(() => {
        if (values?.make && values.make !== selectedVehicleMake) {
            setSelectedVehicleMake(values.make);
        }
    }, [values?.make]);

    useEffect(() => {
        setLoadingMakes(true);
        const timer = setTimeout(() => {
            seachForVehicleMake(vehicleMakePrefix);
        }, 250);
        return () => clearTimeout(timer);
    }, [vehicleMakePrefix]);

    // Auto-fill body type from make + model lookup. Re-runs when either changes;
    // user can manually override afterwards and the override sticks until they
    // pick a different make/model.
    useEffect(() => {
        if (!values.make || !values.model) return;
        const inferred = lookupVehicleBodyType(values.make, values.model);
        if (inferred && values.bodyType !== inferred) {
            setFieldValue('bodyType', inferred);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.make, values.model]);

    const seachForVehicleModel = async () => {
        try {
            if (selectedVehicleMake) {
                let models = await searchVehicleModelsByMake(selectedVehicleMake?.toUpperCase()?.trim());
                if (models?.data) {
                    setVehicleModels(models?.data);
                }
            }
        } catch (error) {
        }
    };

    const vehicleUsages = ()=>{
        return (
            <SelectDropdown
                label="Vehicle Usage"
                name="vehicleUsages"
                listSelectedValue={values.vehicleUsages}
                onChange={(val: string | null) => {
                    setFieldValue('vehicleUsages', val);
                }}
                description="Vehicle Usage"
                onBlur={handleBlur}
                search
                searchPlaceHolder="Search by body type ..."
                list={enums?.vehicleUsages?.map((item: IEnum) => ({
                    name: removeUnderscoresAndCapitalize(item.title),
                    value: item.key,
                }))}
                placeholder="Select vehicle usage"
                disabled={usageGate.disabled}
                error={touched.vehicleUsages ? errors.vehicleUsages : undefined}
                helperText={usageGate.disabled ? usageGate.reason : (touched?.vehicleUsages ? errors.vehicleUsages : undefined)}
                required
            />
        )
    }
    const renderVehicleFields = () => {
        return (
            <>
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-darkBg-mainText flex items-center">
                        <Car className="h-6 w-6 mr-2 text-primary-main" />
                        Vehicle Details
                    </h2>
                    <p className="text-gray-600 dark:text-darkBg-subText mt-1">Please provide information about the vehicle</p>
                </div>

                {/* Basic Vehicle Information */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-4 flex items-center">
                        <Car className="h-5 w-5 mr-2" />
                        Basic Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                        <MultiSelect
                            label="Vehicle Make"
                            selected={[values.make]}
                            onChange={(val: string[]) => {
                                setFieldValue("make", val?.[0]);
                                if (val && val.length > 0) {
                                    setSelectedVehicleMake(val[0]);
                                }
                            }}
                            list={vehicleMakes?.map(
                                (make: any) =>
                                ({
                                    name: make,
                                    value: make,
                                } as IMultiSelectItem)
                            )}
                            placeholder="Search and select your vehicle make"
                            selectInputClassName='!shadow-none'
                            disabled={makeGate.disabled}
                            error={touched.make ? errors.make as string : undefined}
                            helperText={makeGate.disabled ? makeGate.reason : (touched.make ? errors.make as string : undefined)}
                            required
                            description="vehicle make - manufacturer"
                            onQueryChange={(query: string) => { setVehicleMakePrefix(query) }}
                            multiSelect={false}
                            loading={loadingMakes}
                        />

                        <InputSelectMenu
                            name={'model'}
                            label="Vehicle Model"
                            placeholder="Select your vehicle model"
                            description="vehicle model - type"
                            list={vehicleModels}
                            listSelectedValue={values.model}
                            onChange={(val: string) => {
                                setFieldValue("model", val);
                            }}
                            onBlur={handleBlur}
                            disabled={modelGate.disabled}
                            error={touched.model ? errors.model : undefined}
                            helperText={modelGate.disabled ? modelGate.reason : (touched.model ? errors.model : undefined)}
                            required
                            search
                            searchPlaceHolder="Enter your vehicle model. Eg. Yaris"
                            sort
                        />

                        <SelectDropdown
                            label="Vehicle Body Type"
                            name="bodyType"
                            listSelectedValue={values.bodyType}
                            onChange={(val: string | null) => {
                                setFieldValue('bodyType', val);
                            }}
                            description="body type"
                            onBlur={handleBlur}
                            search
                            searchPlaceHolder="Search by body type ..."
                            list={enums?.vehicleBodyTypes?.map((item: IEnum) => ({
                                name: removeUnderscoresAndCapitalize(item.title),
                                value: item.key,
                            }))}
                            placeholder="Select body type"
                            error={touched.bodyType ? errors.bodyType : undefined}
                            helperText={touched?.bodyType ? errors.bodyType : undefined}
                            required
                        />

                        {showVehicleUsages && vehicleUsages()}

                        <InputSelectMenu
                            label="Manufactured Year"
                            name="manufacturedYear"
                            listSelectedValue={values.manufacturedYear}
                            onChange={(val: string | null) => {
                                setFieldValue('manufacturedYear', val);
                            }}
                            onBlur={handleBlur}
                            list={validManufacturingYears}
                            description="The year the vehicle was manufactured"
                            placeholder="Select manufactured year"
                            required
                            sort
                            emptyListMessage="Select manufactured year to proceed"
                            error={touched.manufacturedYear ? errors.manufacturedYear : undefined}
                            helperText={touched.manufacturedYear ? errors.manufacturedYear : undefined}
                        />

                        <Input
                            label={values.powerType === 'hp' ? 'Horse Power' : 'Cubic Capacity'}
                            name="cc"
                            value={values.cc}
                            description="Cubic capacity of the vehicle"
                            append={
                                <MiniSelectMenu
                                    name="powerType"
                                    options={['cc', 'hp']}
                                    value={values.powerType}
                                    onChange={(e: any) => {
                                        const val = e?.target?.value;
                                        if (val) {
                                            setFieldValue('powerType', val);
                                            const currentCC = Number(values.cc) || 0;
                                            if (val === 'cc') {
                                                setFieldValue('cc', Math.round(currentCC * 15));
                                            } else {
                                                setFieldValue('cc', Math.round((currentCC / 15) * 100) / 100);
                                            }
                                        }
                                    }}
                                />
                            }
                            type="number"
                            min={50}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Cubic capacity"
                            error={touched.cc ? errors.cc : undefined}
                            helperText={touched.cc ? errors.cc : undefined}
                            required
                        />


                        <Input
                            type="number"
                            id="maxSeat"
                            label="Number of Passengers"
                            value={values?.maxSeat}
                            description="The number of passengers the vehicle can hold"
                            min={1}
                            onChange={handleChange}
                            placeholder="Enter number of passengers"
                            onBlur={handleBlur}
                            required
                            disabled={dataFromQuoteCalculation?.IS_QUOTE_CALCULATOR ? true : false}
                            error={touched.maxSeat ? errors.maxSeat : undefined}
                            helperText={touched?.maxSeat ? errors.maxSeat : undefined}
                        />

                        <Input
                            type="text"
                            name="chassisNumber"
                            label="Chassis Number / VIN"
                            value={values?.chassisNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter chassis number"
                            error={errors.chassisNumber && touched.chassisNumber ? errors.chassisNumber : undefined}
                            helperText={errors.chassisNumber && touched.chassisNumber ? errors.chassisNumber : undefined}
                            required
                        />

                        <Input
                            type="text"
                            id="registrationNumber"
                            label="Registration Number"
                            placeholder={'Enter registration number'}
                            value={values?.registrationNumber?.toUpperCase()}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched?.registrationNumber && errors.registrationNumber ? errors.registrationNumber : undefined}
                            helperText={touched?.registrationNumber && errors.registrationNumber ? errors.registrationNumber : undefined}
                            required
                        />

                        <InputSelectMenu
                            label="Registration Year"
                            name="registrationYear"
                            listSelectedValue={values.registrationYear}
                            onChange={(val: string | null) => {
                                setFieldValue('registrationYear', val);
                            }}
                            onBlur={handleBlur}
                            list={validManufacturingYears}
                            placeholder="Select registration year"
                            required
                            disabled={dataFromQuoteCalculation?.IS_QUOTE_CALCULATOR ? true : false}
                            sort
                            emptyListMessage="Select registration year to proceed"
                            error={touched.registrationYear ? errors.registrationYear : undefined}
                            helperText={touched.registrationYear ? errors.registrationYear : undefined}
                        />

                        {shouldShowSumInsured && (
                            <Input
                                type="number"
                                id="sumInsured"
                                label={`Vehicle's Insured Value`}
                                value={values?.sumInsured}
                                onChange={handleChange}
                                placeholder="Enter vehicle insured value"
                                onBlur={handleBlur}
                                disabled={sumInsuredGate.disabled || (dataFromQuoteCalculation?.IS_QUOTE_CALCULATOR ? true : false)}
                                error={touched.sumInsured ? errors.sumInsured : undefined}
                                helperText={sumInsuredGate.disabled ? sumInsuredGate.reason : (touched?.sumInsured ? errors.sumInsured : undefined)}
                            />
                        )}

                        <Input
                            label="Vehicle Colour"
                            name="color"
                            value={values.color}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter vehicle colour"
                            error={touched.color ? errors.color : undefined}
                            helperText={touched.color ? errors.color : undefined}
                            required
                        />
                    </div>
                </div>


                {/* Interested Parties */}
                {showInterestedPartiesFields && (
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-1 flex items-center">
                            <Users className="h-5 w-5 mr-2" />
                            Vehicle Ownership
                        </h3>
                        <h4 className="text-base font-semibold text-gray-800 dark:text-darkBg-mainText mb-1">
                            Add Co-Insured
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-darkBg-subText mb-4">
                            This section is to designate individuals or entities authorized to manage the policy or initiate a claim in the primary insured&apos;s absence.
                        </p>

                        <div className="space-y-8 md:space-y-0  grid min-[1280px]:grid-cols-2 md:gap-4 mb-4">
                            <div className='max-[1280px]:grid max-[1280px]:grid-cols-2'>
                                <SelectDropdown
                                    name="composerIsOwner"
                                    label={`Are you the registered owner of the vehicle in the ${getDriverLicenseProviderByCountry(selectedCountry?.iso)} records?`}
                                    list={[
                                        {
                                            name: 'Yes',
                                            value: 'yes'
                                        },
                                        {
                                            name: 'No, Another person',
                                            value: 'no'
                                        }
                                    ]}
                                    listSelectedValue={
                                        values.composerIsOwner !== undefined
                                            ? values.composerIsOwner === 'yes'
                                                ? 'yes'
                                                : 'no'
                                            : undefined
                                    }
                                    onChange={(val: string | null) => {
                                        setFieldValue('composerIsOwner', val);
                                    }}
                                    onBlur={handleBlur}
                                    classes='!min-w-[290px] md:w-auto'
                                    labelClasses='!min-w-[290px] md:w-auto'
                                    error={touched.composerIsOwner ? errors.composerIsOwner : undefined}
                                    helperText={touched?.composerIsOwner ? errors.composerIsOwner : undefined}
                                    required
                                    sort
                                />
                            </div>

                            <div className='max-[1280px]:grid max-[1280px]:grid-cols-2'>
                                {values.composerIsOwner === 'no' && (
                                    <SelectMenu
                                        label="What is the nature of your interest in the vehicle?"
                                        name="natureOfInterest"
                                        listSelectedValue={values.natureOfInterest}
                                        onChange={(val: any) => {
                                            setFieldValue('natureOfInterest', val);
                                        }}
                                        onBlur={handleBlur}
                                        list={getNatureOfInterestListByCountry(selectedCountry?.iso)}
                                        placeholder=""
                                        labelClasses='!min-w-[290px] md:w-auto'
                                        wrapperClasses='!min-w-[290px] md:w-auto'
                                        error={touched?.natureOfInterest ? errors.natureOfInterest : undefined}
                                        helperText={touched?.natureOfInterest ? errors.natureOfInterest : undefined}
                                    />
                                )}
                            </div>
                        </div>

                        {/* If registered owner different from composer */}
                        {values.composerIsOwner === 'no' && values.natureOfInterest && values.natureOfInterest !== getDVLAOrFRSC(selectedCountry?.iso) && (
                            <div className="space-y-4">
                                <div className={`space-y-8 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-4`}>
                                    <Input
                                        type="text"
                                        id="owner"
                                        name="owner"
                                        label="Vehicle Co-owner's Name"
                                        placeholder="Eg. Kwaku Manu"
                                        className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                                        value={values?.owner}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched?.owner ? errors.owner : undefined}
                                        helperText={touched?.owner ? errors.owner : undefined}
                                    />

                                    <Input
                                        type="text"
                                        name="ownerAddress"
                                        label="Owner's Address"
                                        placeholder="Eg. Old barrier, Kasoa"
                                        value={values.ownerAddress}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched?.ownerAddress ? errors.ownerAddress : undefined}
                                        helperText={touched?.ownerAddress ? errors.ownerAddress : undefined}
                                    />

                                    <IntlPhoneInput
                                        label="Owner's Phone"
                                        placeholder="Eg. Kwaku Manu"
                                        value={values.ownerPhone}
                                        onChange={(val: any) => {
                                            setFieldValue('ownerPhone', val);
                                        }}
                                        country={selectedCountry?.iso}
                                        onBlur={handleBlur}
                                        error={touched?.ownerPhone ? errors.ownerPhone : undefined}
                                        helperText={touched?.ownerPhone ? errors.ownerPhone : undefined}
                                    />
                                </div>

                                <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-4">
                                    <InputRadio
                                        name="hasInterestedParties"
                                        label={'Is this vehicle on hire purchase?'}
                                        options={[
                                            { name: 'Yes', value: 'yes' },
                                            { name: 'No', value: 'no' }
                                        ]}
                                        selected={
                                            values.hasInterestedParties !== undefined
                                                ? values.hasInterestedParties === 'yes'
                                                    ? 'yes'
                                                    : 'no'
                                                : undefined
                                        }
                                        onSelect={handleChange}
                                        error={
                                            touched?.hasInterestedParties
                                                ? errors.hasInterestedParties as string
                                                : undefined
                                        }
                                        helperText={touched?.hasInterestedParties ? errors.hasInterestedParties as string : undefined}
                                    />

                                    {(values.hasInterestedParties === 'yes') && (
                                        <Input
                                            type="text"
                                            id="interestedParties"
                                            label="If yes, provide name of interested party."
                                            placeholder=""
                                            className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                                            value={values.interestedParties ?? ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched?.interestedParties ? errors.interestedParties : undefined}
                                            helperText={touched?.interestedParties ? errors.interestedParties : undefined}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}


                {/* Named Drivers */}
                {showNamedDriverFields && (() => {
                    const drivers: Array<{ name?: string; phone?: string; email?: string; license?: string; hasComplications?: 'yes' | 'no' | ''; complications?: string }> = Array.isArray(values.namedDrivers) && values.namedDrivers.length > 0
                        ? values.namedDrivers
                        : [{ name: values.driver ?? '', hasComplications: values.driverHasComplications === 'yes' ? 'yes' : values.driverHasComplications === 'no' ? 'no' : '', complications: values.driverComplications ?? '' }];
                    const updateDriver = (idx: number, patch: Partial<typeof drivers[number]>) => {
                        const next = drivers.map((d, i) => (i === idx ? { ...d, ...patch } : d));
                        setFieldValue('namedDrivers', next);
                        if (idx === 0) {
                            if ('name' in patch) setFieldValue('driver', patch.name ?? '');
                            if ('phone' in patch) setFieldValue('driverPhone', patch.phone ?? '');
                            if ('email' in patch) setFieldValue('driverEmail', patch.email ?? '');
                            if ('license' in patch) setFieldValue('driverLicense', patch.license ?? '');
                            if ('hasComplications' in patch) setFieldValue('driverHasComplications', patch.hasComplications ?? '');
                            if ('complications' in patch) setFieldValue('driverComplications', patch.complications ?? '');
                        }
                    };
                    const addDriver = () => {
                        const next = [...drivers, { name: '', hasComplications: '' as const, complications: '' }];
                        setFieldValue('namedDrivers', next);
                    };
                    const removeDriver = (idx: number) => {
                        if (drivers.length <= 1) return;
                        const next = drivers.filter((_, i) => i !== idx);
                        setFieldValue('namedDrivers', next);
                        const primary = next[0];
                        setFieldValue('driver', primary?.name ?? '');
                        setFieldValue('driverHasComplications', primary?.hasComplications ?? '');
                        setFieldValue('driverComplications', primary?.complications ?? '');
                    };

                    return (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-4 flex items-center">
                                <Users className="h-5 w-5 mr-2" />
                                Named Drivers
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-darkBg-subText mb-4">
                                Add additional drivers who will regularly use this vehicle.
                            </p>

                            <div className="space-y-6">
                                {drivers.map((driver, idx) => (
                                    <div key={idx} className="border border-gray-200 dark:border-darkBg-border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="text-sm font-semibold text-gray-800 dark:text-darkBg-mainText">
                                                {idx === 0 ? 'Main Driver' : `Driver ${idx + 1}`}
                                            </h4>
                                            {drivers.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeDriver(idx)}
                                                    className="text-sm text-danger-main hover:text-danger-hover flex items-center gap-1"
                                                    aria-label={`Remove driver ${idx + 1}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                    Remove
                                                </button>
                                            )}
                                        </div>

                                        <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-4">
                                            <Input
                                                type="text"
                                                id={`namedDrivers.${idx}.name`}
                                                label={idx === 0 ? 'Vehicle Main Driver' : 'Driver Name'}
                                                placeholder="Eg. Francis Atuahene"
                                                className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                                                value={driver.name ?? ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateDriver(idx, { name: e.target.value })}
                                                onBlur={handleBlur}
                                            />

                                            <IntlPhoneInput
                                                label="Driver's Phone"
                                                placeholder="Eg. 024 000 0000"
                                                value={driver.phone ?? ''}
                                                onChange={(val: any) => updateDriver(idx, { phone: val })}
                                                country={selectedCountry?.iso}
                                                onBlur={handleBlur}
                                            />

                                            <Input
                                                type="text"
                                                id={`namedDrivers.${idx}.email`}
                                                name={`namedDrivers.${idx}.email`}
                                                label="Driver's Email"
                                                placeholder="Eg. driver@email.com"
                                                className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                                                value={driver.email ?? ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateDriver(idx, { email: e.target.value })}
                                                onBlur={handleBlur}
                                            />

                                            <Input
                                                type="text"
                                                id={`namedDrivers.${idx}.license`}
                                                name={`namedDrivers.${idx}.license`}
                                                label="Driver's License Number"
                                                placeholder="Eg. License number"
                                                className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                                                value={driver.license ?? ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateDriver(idx, { license: e.target.value })}
                                                onBlur={handleBlur}
                                            />

                                            <div className="md:col-span-2 space-y-2">
                                                <InputRadio
                                                    name={`namedDrivers.${idx}.hasComplications`}
                                                    selected={driver.hasComplications === 'yes' ? 'yes' : driver.hasComplications === 'no' ? 'no' : ''}
                                                    label="Does driver have any disease or complications?"
                                                    options={[
                                                        { name: 'Yes', value: 'yes' },
                                                        { name: 'No', value: 'no' }
                                                    ]}
                                                    isFlex
                                                    onSelect={(e: React.ChangeEvent<HTMLInputElement>) => updateDriver(idx, { hasComplications: e.target.value as 'yes' | 'no' })}
                                                />

                                                {driver.hasComplications === 'yes' && (
                                                    <TextArea
                                                        name={`namedDrivers.${idx}.complications`}
                                                        id={`namedDrivers.${idx}.complications`}
                                                        placeholder="Please enter disease or complications of this driver."
                                                        rows={1}
                                                        className="border border-gray-200 bg-transparent rounded-[0px] placeholder-[#848484] focus:ring-0 px-4 py-3 focus:outline-none resize-none"
                                                        value={driver.complications ?? ''}
                                                        onValueChanged={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateDriver(idx, { complications: e.target.value })}
                                                        onFocusOut={() => { /* no-op, handled by onValueChanged */ }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    onClick={addDriver}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-primary-main text-gray-900 dark:text-darkBg-mainText bg-primary-surface dark:bg-darkBg hover:bg-primary-main/20 transition-colors"
                                >
                                    <Plus className="h-4 w-4" />
                                    Add Another Driver
                                </button>
                            </div>
                        </div>
                    );
                })()}

                {/* Vehicle Modifications */}
                {showModificationFields && (
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-4 flex items-center">
                            <Settings className="h-5 w-5 mr-2" />
                            Vehicle Modifications
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                            <div className="space-y-4 ">
                                <InputRadio
                                    id="isVehicleAltered"
                                    name="isVehicleAltered"
                                    options={[
                                        { name: 'Yes', value: 'yes' },
                                        { name: 'No', value: 'no' }
                                    ]}
                                    label="Has this vehicle been altered or modified in any way?"
                                    selected={
                                        values.isVehicleAltered !== undefined
                                            ? values.isVehicleAltered === 'yes'
                                                ? 'yes'
                                                : 'no'
                                            : undefined
                                    }
                                    isFlex
                                    onSelect={handleChange}
                                    error={touched?.isVehicleAltered ? errors.isVehicleAltered as string : undefined}
                                    helperText={touched?.isVehicleAltered ? errors.isVehicleAltered as string : undefined}
                                />

                                {values?.isVehicleAltered === 'yes' && (
                                    <TextArea
                                        id="alterationDetails"
                                        placeholder="Please enter vehicle alteration details"
                                        className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                                        value={
                                            values?.alterationDetails
                                                ? values.alterationDetails
                                                : undefined
                                        }
                                        rows={1}
                                        onValueChanged={handleChange}
                                        onFocusOut={handleBlur}
                                        error={touched?.alterationDetails ? errors.alterationDetails as string : undefined}
                                        helperText={touched?.alterationDetails ? errors.alterationDetails as string : undefined}
                                    />
                                )}
                            </div>

                            <div className="space-y-4">
                                <InputRadio
                                    name="isInGoodRepairState"
                                    label={'Is your vehicle in a good state of repair?'}
                                    options={[
                                        { name: 'Yes', value: 'yes' },
                                        { name: 'No', value: 'no' }
                                    ]}
                                    selected={
                                        values.isInGoodRepairState !== undefined
                                            ? values.isInGoodRepairState === 'yes'
                                                ? 'yes'
                                                : 'no'
                                            : undefined
                                    }
                                    isFlex
                                    onSelect={handleChange}
                                    error={touched?.isInGoodRepairState ? errors.isInGoodRepairState as string : undefined}
                                    helperText={touched?.isInGoodRepairState ? errors.isInGoodRepairState as string : undefined}
                                />

                                {values.isInGoodRepairState === 'no' && (
                                    <TextArea
                                        name="repairState"
                                        id="repairState"
                                        placeholder="Please enter repair state of vehicle."
                                        rows={1}
                                        className="border border-gray-200 bg-transparent rounded-[0px] placeholder-[#848484] focus:ring-0 px-4 py-3 focus:outline-none resize-none"
                                        value={values.repairState ?? ''}
                                        onValueChanged={handleChange}
                                        onFocusOut={handleChange}
                                        error={touched?.repairState ? errors.repairState as string : undefined}
                                        helperText={touched?.repairState ? errors.repairState as string : undefined}
                                    />
                                )}
                            </div>
                        </div>

                        <div className=" space-y-4 mt-8">
                            <InputRadio
                                id="hasInstalledAccessories"
                                name="hasInstalledAccessories"
                                options={[
                                    { name: 'Yes', value: 'yes' },
                                    { name: 'No', value: 'no' }
                                ]}
                                label="Have you installed any extra accessories that you would like to insure separately?"
                                selected={
                                    values.hasInstalledAccessories !== undefined
                                        ? values.hasInstalledAccessories === 'yes'
                                            ? 'yes'
                                            : 'no'
                                        : undefined
                                }
                                isFlex
                                onSelect={handleChange}
                                error={touched?.hasInstalledAccessories ? errors.hasInstalledAccessories as string : undefined}
                                helperText={touched?.hasInstalledAccessories ? errors.hasInstalledAccessories as string : undefined}
                            />

                            {values.hasInstalledAccessories === 'yes' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                    <Input
                                        type="text"
                                        id="installedAccessoriesDetails"
                                        placeholder="If yes, please provide details of accessories"
                                        className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                                        value={values.installedAccessoriesDetails ?? ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched?.installedAccessoriesDetails ? errors.installedAccessoriesDetails : undefined}
                                        helperText={touched?.installedAccessoriesDetails ? errors.installedAccessoriesDetails : undefined}
                                    />

                                    <Input
                                        type="number"
                                        id="installedAccessoriesValue"
                                        placeholder="Please enter insured value of accessories"
                                        className="rounded-[0px] placeholder-[#848484] focus:ring-primary-border px-3"
                                        value={values.installedAccessoriesValue ?? ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched?.installedAccessoriesValue ? errors.installedAccessoriesValue : undefined}
                                        helperText={touched?.installedAccessoriesValue ? errors.installedAccessoriesValue : undefined}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </>
        )
    }

    const renderOnlyMakeModelfields = () => {
        return(
            <>
                    <MultiSelect
                            label="Vehicle Make"
                            selected={[values.make ? values.make : setFields && setFields?.length > 0 ? setFields?.find((field)=>field.fieldName==='make')?.value : undefined]}
                            onChange={(val: string[]) => {
                                setFieldValue("make", val?.[0]);
                                if (val && val.length > 0) {
                                    setSelectedVehicleMake(val[0]);
                                }
                            }}
                            list={vehicleMakes?.map(
                                (make: any) =>
                                ({
                                    name: make,
                                    value: make,
                                } as IMultiSelectItem)
                            )}
                            placeholder="Search and select your vehicle make"
                            selectInputClassName='!shadow-none'
                            error={touched.make ? errors.make as string : undefined}
                            helperText={touched.make ? errors.make as string : undefined}
                            required
                            description="vehicle make - manufacturer"
                            onQueryChange={(query: string) => { setVehicleMakePrefix(query) }}
                            multiSelect={false}
                            loading={loadingMakes}
                            disabled={disabledFields?.find((field)=>field.fieldName==='make')?.disabled}
                        />

                        <InputSelectMenu
                            name={'model'}
                            label="Vehicle Model"
                            placeholder="Select your vehicle model"
                            description="vehicle model - type"
                            list={vehicleModels}
                            listSelectedValue={values.model}
                            onChange={(val: string) => {
                                setFieldValue("model", val);
                            }}
                            onBlur={handleBlur}
                            error={touched.model ? errors.model : undefined}
                            helperText={touched.model ? errors.model : undefined}
                            required
                            disabled={disabledFields?.find((field)=>field.fieldName==='model')?.disabled}
                            search
                            searchPlaceHolder="Enter your vehicle model. Eg. Yaris"
                            sort
                        />
            </>
        )
    }

    return (
        <>
            {!showOnlyMakeModelfields && renderVehicleFields()}
            {showOnlyMakeModelfields && renderOnlyMakeModelfields()}
        </>
    );
};

export default VehicleInformationForm;